// Shared YouTube embed used across Tutorials, FAQ admin, and Documents.
//
// Approach: YouTube IFrame Player API (singleton load) + full-cover overlay
// whenever the video is not actively playing (paused, ended, cued, unstarted).
// The overlay completely covers the related-video grid that YouTube forces when
// the video is paused or ended, while still allowing the user to resume by
// clicking our own Play button.
//
// When the video IS playing the overlay is hidden so native controls work
// normally. A thin top blocker (left side only) stays visible at all times to
// prevent the channel/title hover link from navigating away.

import { useEffect, useRef, useState, useId } from 'react';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

// ---------------------------------------------------------------------------
// IFrame API singleton loader
// ---------------------------------------------------------------------------

// Queue of callbacks to fire once YT.Player is available.
const _ytReadyCallbacks = [];
let _ytApiLoading = false;

function loadYouTubeApi(callback) {
    if (window.YT && window.YT.Player) {
        // API already fully initialised.
        callback();
        return;
    }

    _ytReadyCallbacks.push(callback);

    if (_ytApiLoading) {
        // Script is already in flight — just wait.
        return;
    }

    // Chain onto any existing onYouTubeIframeAPIReady set by another library.
    const existingReady = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = function () {
        if (typeof existingReady === 'function') existingReady();
        const cbs = _ytReadyCallbacks.splice(0);
        cbs.forEach((cb) => cb());
    };

    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    tag.async = true;
    document.head.appendChild(tag);
    _ytApiLoading = true;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function YouTubeEmbed({ url, maxWidth = 480 }) {
    const reactId = useId();
    // Sanitise the id to be a valid DOM element id (no colons from useId).
    const containerId = 'yt-embed-' + reactId.replace(/[^a-zA-Z0-9-_]/g, '_');

    const id = url?.match(/(?:youtu\.be\/|v=|\/embed\/|\/shorts\/)([a-zA-Z0-9_-]{11})/)?.[1];

    // playing === true only when YT.PlayerState.PLAYING (1).
    const [playing, setPlaying] = useState(false);
    // ready === true once the player has fired onReady (playVideo is safe).
    const [ready, setReady] = useState(false);

    const playerRef = useRef(null);

    useEffect(() => {
        if (!id) return;

        // Per-effect-run flag. Under React StrictMode the effect mounts twice;
        // using a LOCAL flag (not a shared ref) ensures the stale first run's
        // queued init is cancelled and only ONE valid player is created.
        let cancelled = false;
        let localPlayer = null;
        let pollId = null;

        function initPlayer() {
            if (cancelled || localPlayer) return;

            localPlayer = new window.YT.Player(containerId, {
                videoId: id,
                host: 'https://www.youtube-nocookie.com',
                playerVars: {
                    modestbranding: 1,
                    rel: 0,
                    iv_load_policy: 3,
                    playsinline: 1,
                    fs: 0,
                    controls: 1,
                    enablejsapi: 1,
                    origin: window.location.origin,
                },
                events: {
                    onReady: (event) => {
                        if (cancelled) return;
                        setReady(true);
                        // Force the YT-created iframe to sit BELOW our overlay
                        // and fill the frame (YT may drop our inline styles).
                        try {
                            const f = event.target.getIframe();
                            f.style.position = 'absolute';
                            f.style.inset = '0';
                            f.style.width = '100%';
                            f.style.height = '100%';
                            f.style.zIndex = '1';
                        } catch (_) { /* ignore */ }
                    },
                    onStateChange: (event) => {
                        // PlayerState: -1 unstarted, 0 ended, 1 playing,
                        //              2 paused,   3 buffering, 5 cued.
                        // Treat 1 (playing) and 3 (buffering) as "playing" to
                        // avoid a flash of the overlay during seek buffering.
                        const isPlaying =
                            event.data === window.YT.PlayerState.PLAYING ||
                            event.data === window.YT.PlayerState.BUFFERING;
                        setPlaying(isPlaying);
                    },
                },
            });
            playerRef.current = localPlayer;

            // Polling fallback: reliably reflect pause/end even if an
            // onStateChange event is missed, so the cover overlay always shows.
            pollId = window.setInterval(() => {
                if (cancelled || !localPlayer || typeof localPlayer.getPlayerState !== 'function') return;
                const st = localPlayer.getPlayerState();
                setPlaying(st === window.YT.PlayerState.PLAYING || st === window.YT.PlayerState.BUFFERING);
            }, 400);
        }

        loadYouTubeApi(initPlayer);

        return () => {
            cancelled = true;
            if (pollId) window.clearInterval(pollId);
            if (localPlayer && typeof localPlayer.destroy === 'function') {
                try {
                    localPlayer.destroy();
                } catch (_) {
                    // Ignore destroy errors on unmount.
                }
            }
            if (playerRef.current === localPlayer) playerRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, containerId]);

    // Non-YouTube fallback.
    if (!id) {
        return (
            <a href={url} target="_blank" rel="noreferrer" style={{ color: '#1A8CFF', fontSize: '0.8rem' }}>
                {url}
            </a>
        );
    }

    function handleOverlayPlay() {
        if (ready && playerRef.current && typeof playerRef.current.playVideo === 'function') {
            playerRef.current.playVideo();
        }
    }

    return (
        <div
            style={{
                position: 'relative',
                width: '100%',
                maxWidth,
                aspectRatio: '16/9',
                marginTop: 8,
                borderRadius: 8,
                overflow: 'hidden',
                background: '#000',
            }}
        >
            {/* The API replaces this div with the iframe. */}
            <div
                id={containerId}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
            />

            {/* Full-cover overlay: shown whenever video is NOT playing.
                Prevents any related-video grid, end-card links, or paused-state
                UI from being clickable. The user clicks here to resume. */}
            {!playing && (
                <div
                    onClick={handleOverlayPlay}
                    style={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 20,
                        background: 'rgba(0,0,0,0.35)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                    }}
                >
                    <div
                        style={{
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            background: 'rgba(255,255,255,0.90)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.4)',
                        }}
                    >
                        <PlayArrowIcon style={{ fontSize: 40, color: '#111', marginLeft: 3 }} />
                    </div>
                </div>
            )}

            {/* Thin top-left blocker: always present while playing to stop the
                hovering channel/title bar from navigating to youtube.com.
                Leaves the top-right ~110px alone so volume/CC/settings work. */}
            <div
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 110,
                    height: 48,
                    background: 'transparent',
                    cursor: 'default',
                    zIndex: 15,
                }}
            />

            {/* Full-width bottom-bar blocker: always present. Covers the entire
                bottom control bar where YouTube places "More videos", the
                share / copy-link icon, the channel watermark and the YouTube
                logo — none of which can be clicked. (Seeking via the progress
                bar is intentionally disabled to prevent any navigation away.)
                Play/pause is the centre button; volume/CC/settings are top-right. */}
            <div
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 60,
                    background: 'transparent',
                    cursor: 'default',
                    zIndex: 15,
                }}
            />
        </div>
    );
}
